import { WorkflowInputFilter } from "domain/models/workflow-input-filter";
import { useWorkflowByFilter } from "presentation/hooks/use-query-workflow";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useFilterDashboardStore } from "presentation/stores/use-filter-dashboard-store";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTableSelectionMultipleChangeEvent,
  DataTableValueArray,
} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HeaderDashboardToApprovalGrid from "./HeaderDashboardToApprovalGrid";
import TaskApprovalForm from "./TaskApprovalForm";
import DocumentTypeDropdown from "../DocumentTypeDropdown";
import Spinner from "presentation/components/Shared/Spinner";

interface Task {
  id: string;
  employeeId: string;
  type: string;
  description: string;
  dateRef?: Date;
  status: string;
}

interface ParamProps {
  level: number;
}

const DashboardToApprovalGrid: React.FC<ParamProps> = (params: ParamProps) => {
  const { filters, setFilters } = useFilterDashboardStore();
  const { userEmployee } = useAuthStore();
  const [selectedTasks, setSelectedTasks] = useState<DataTableValueArray>([]);
  const [documentKey, setDocumentKey] = useState<Task>();
  const [showModal, setShowModal] = useState(false);
  const dt = useRef<DataTable<DataTableValueArray>>(null);

  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    // sortBy: "id",
    // sortOrder: "desc",
    // limit: 50,
    // page: 1,
    filters: {},
  });

  const {
    data: tasksToApproval,
    refetch: refetchWorkflowToApproval,
    isLoading: isLoadingTasks,
    isFetching: isFetchingTasks,
  } = useWorkflowByFilter({
    // sortBy: lazyParams.sortBy,
    // sortOrder: lazyParams.sortOrder,
    // page: lazyParams.page,
    // limit: lazyParams.limit,
    ...lazyParams.filters,
    employeeId: userEmployee?.id,
    level: params.level,
  } as WorkflowInputFilter);

  const refreshGrid = useCallback(async () => {
    await refetchWorkflowToApproval();
  }, [refetchWorkflowToApproval]);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  const handleCloseModal = useCallback(async () => {
    await refetchWorkflowToApproval();
    setShowModal(false);
  }, [refetchWorkflowToApproval]);

  const handleVacationReport = useCallback(async (rowData: Task) => {
    const regex = /(\d{2}\/\d{2}\/\d{4})/g;
    const dates = rowData.description.match(regex);
    if (dates && dates.length > 0) {
      const partialDate = dates[0].split("/");
      rowData.dateRef = new Date(
        parseInt(partialDate[2]),
        parseInt(partialDate[1]) - 1,
        parseInt(partialDate[0])
      );
    }
    setDocumentKey(rowData);
    setShowModal(true);
  }, []);

  const handleGenerateReport = useCallback(async (rowData: Task) => {
    setDocumentKey(rowData);
    setShowModal(true);
  }, []);

  const showActionOptions = useCallback(
    (rowData: Task) => {
      return (
        <div className="spacing-icons">
          {!rowData.type.includes("Férias") && (
            <i
              className="pi pi-file-check cursor-pointer"
              onClick={() => handleGenerateReport(rowData)}
            />
          )}{" "}
          {rowData.type.includes("Férias") && (
            <i
              className="pi pi-file-check cursor-pointer"
              onClick={() => handleVacationReport(rowData)}
            />
          )}{" "}
        </div>
      );
    },
    [handleGenerateReport, handleVacationReport]
  );

  // Mostrar loading quando dados estão carregando
  if (isLoadingTasks) {
    return <Spinner />;
  }

  return (
    <div style={{ position: "relative" }}>
      {/* Loading overlay durante fetching em background */}
      {isFetchingTasks && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            borderRadius: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <i
              className="pi pi-spin pi-spinner"
              style={{ fontSize: "1.5rem", color: "#2196f3" }}
            />
            <span style={{ color: "#2196f3", fontWeight: "500" }}>
              Atualizando dados...
            </span>
          </div>
        </div>
      )}

      <DataTable
        ref={dt}
        value={tasksToApproval?.data}
        selectionMode={"checkbox"}
        selection={selectedTasks}
        onSelectionChange={(
          e: DataTableSelectionMultipleChangeEvent<DataTableValueArray>
        ) => {
          setSelectedTasks(e.value);
          console.log("📋 Itens selecionados:", e.value);
          console.log("📊 Total de itens selecionados:", e.value.length);
          if (e.value.length > 0) {
            console.table(
              e.value.map((item: any) => ({
                ID: item.id,
                Tipo: item.type,
                Descrição: item.description,
                Status: item.status,
              }))
            );
          }
        }}
        size="small"
        header={
          <HeaderDashboardToApprovalGrid
            level={params.level}
            tasks={selectedTasks}
            refresh={refreshGrid}
            dt={dt}
          />
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        dataKey="id"
      >
        {params.level === 2 && (
          <Column
            selectionMode="multiple"
            headerStyle={{ width: "3rem" }}
          ></Column>
        )}
        <Column
          field="type"
          header="Tipo"
          sortable
          filter
          showFilterMatchModes={false}
          headerStyle={{ width: "10rem" }}
          filterElement={(options) => (
            <DocumentTypeDropdown options={options} />
          )}
        />
        <Column
          field="description"
          header="Descrição"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Dialog
        header={`Documento a assinar: ${documentKey?.type}`}
        visible={showModal}
        style={{ width: "80vw", height: "90vh" }}
        onHide={handleCloseModal}
      >
        <TaskApprovalForm
          // url={url}
          document={documentKey}
          onClose={handleCloseModal}
        />
      </Dialog>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default DashboardToApprovalGrid;
